import { Anchor, Box, Button } from "grommet";
import React from "react";
import { Document, Page } from "react-pdf";
import { Account } from "../utils/AccountStore";
import { Attachment, IsRenderable, Questionnaire } from "../utils/ServiceManagerAPI";
import SkeletonWrapper from "./SkeltonWrapper";
import StyledModal from "./StyledModel";

import { FcFile } from "react-icons/fc";
import { HiTrash } from "react-icons/hi";
import { msalInstance } from "../index";
import { getTokenPopup } from "../utils/HttpClient";
import ConfirmButton from "./ConfirmButton";

const RenderAttachments = ({ questionnaire, attachments, user, deleteAttachment }: { questionnaire?: Questionnaire; attachments: Attachment[]; user: Account; deleteAttachment?: Function }) => {
    const [showAttachment, setShowAttachment] = React.useState(false);
    const [selectedAttachment, setSelectedAttachment] = React.useState<Attachment>();
    const [objectUrl, setObjectUrl] = React.useState<string>();

    const [token, setToken] = React.useState<string>();

    React.useEffect(() => {
        const account = msalInstance.getActiveAccount();
        if (account) {
            getTokenPopup(account).then((token) => {
                setToken(token.accessToken);
            });
        }
    }, []);

    // Function to fetch attachment with authorization headers
    const fetchAttachmentBlob = React.useCallback(
        async (attachment: Attachment) => {
            if (!token) return;

            try {
                const response = await fetch(`${ServiceManagerUrl}api/Attachments/${attachment.id}`, {
                    headers: {
                        Authorization: `IdentityBearer ${token}`,
                    },
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    setObjectUrl(url);
                }
            } catch (error) {
                console.error("Failed to fetch attachment:", error);
            }
        },
        [token]
    );

    // Cleanup blob URLs on unmount
    React.useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    const select = (attachment: Attachment) => {
        setSelectedAttachment(attachment);
        setShowAttachment(true);
        if (IsRenderable(attachment.contentType)) {
            setLoading(true);
            // For non-PDF files, fetch with authorization headers
            if (attachment.contentType !== "application/pdf") {
                fetchAttachmentBlob(attachment);
            }
        }
    };

    const [loading, setLoading] = React.useState(false);

    const toggleAttachmentModal = () => {
        if (showAttachment) {
            setSelectedAttachment(undefined);
            // Cleanup blob URL to prevent memory leaks
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                setObjectUrl(undefined);
            }
        }
        setShowAttachment(!showAttachment);
    };

    const [pages, setPages] = React.useState(0);

    const doDelete = () => {
        if (selectedAttachment) {
            deleteAttachment && deleteAttachment(selectedAttachment.questionID, selectedAttachment.id);
        }
        toggleAttachmentModal();
    };

    return (
        <>
            {(user.hasQuestionnaireAccess(questionnaire?.id, 8) || user.hasCampaignAccess(questionnaire?.campaignID, 16)) && (
                <Box gap={"small"}>
                    {attachments?.map((attachment, attachmentKey) => (
                        <Box direction="row" key={attachment.id}>
                            <Box gap="small">
                                <Anchor label={attachment.name} icon={<FcFile size="18px" />} onClick={() => select(attachment)}></Anchor>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
            <StyledModal isOpen={showAttachment} setIsOpen={() => toggleAttachmentModal()}>
                {selectedAttachment && (
                    <Box width={"large"} gap={"small"}>
                        <Anchor href={`/api/Attachments/${selectedAttachment.id}`}>Download {selectedAttachment.name}</Anchor>
                        {!questionnaire?.locked && deleteAttachment && user.hasQuestionnaireAccess(questionnaire?.id, 8) && selectedAttachment.userID == user.id && (
                            <ConfirmButton label="Delete Attachment" color="status-critical" icon={<HiTrash size="18px" />} onClick={() => doDelete()}></ConfirmButton>
                        )}
                        {IsRenderable(selectedAttachment.contentType) && (
                            <>
                                {selectedAttachment.contentType == "application/pdf" && (
                                    <Document
                                        file={`${ServiceManagerUrl}api/Attachments/${selectedAttachment.id}`}
                                        onLoadSuccess={({ numPages }: { numPages: number }) => {
                                            setPages(numPages);
                                            setLoading(false);
                                        }}
                                        options={{
                                            httpHeaders: {
                                                Authorization: `IdentityBearer ${token}`,
                                            },
                                        }}
                                    >
                                        {Array.from(new Array(pages), (el, index) => (
                                            <Page pageNumber={index + 1} renderTextLayer={false} renderAnnotationLayer={false} width={750} />
                                        ))}
                                    </Document>
                                )}
                                {selectedAttachment.contentType != "application/pdf" && objectUrl && (
                                    <object onLoad={() => setLoading(false)} data={objectUrl} type={selectedAttachment.contentType} width="100%" height="100%"></object>
                                )}
                                <SkeletonWrapper loading={loading}>
                                    <></>
                                </SkeletonWrapper>
                            </>
                        )}
                        <Button onClick={() => toggleAttachmentModal()} label="Close"></Button>
                    </Box>
                )}
            </StyledModal>
        </>
    );
};

export default RenderAttachments;
